import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { DayPicker } from 'react-day-picker';
import { INSPECT_DUE_BEFORE_NOW, INSPECT_DUE_FROM_NOW } from 'config/constants';
import 'react-day-picker/src/style.css';
import './DateFilterPopover.css';

var moment = require('moment');

var POPOVER_WIDTH = 360;
var POPOVER_OFFSET = 6;
var VIEWPORT_MARGIN = 8;
var DISPLAY_FORMAT = 'MM/DD/YYYY';

// Parse a stored column-filter value back into calendar dates. The value is
// either a single "MM/DD/YYYY" or a range "MM/DD/YYYY - MM/DD/YYYY".
function parseFilterValue(value) {
  if (!value || typeof value !== 'string') return { from: null, to: null };
  function toDate(str) {
    var s = str ? str.trim() : '';
    if (!s) return null;
    var m = moment(s, DISPLAY_FORMAT, true);
    return m.isValid() ? m.toDate() : null;
  }
  var parts = value.split('-');
  if (parts.length >= 2) {
    return { from: toDate(parts[0]), to: toDate(parts[1]) };
  }
  return { from: toDate(parts[0]), to: null };
}

// Translate a selected range into the stored value. A single day (or a range
// whose endpoints match) collapses to "MM/DD/YYYY"; a true range becomes
// "MM/DD/YYYY - MM/DD/YYYY". buildHistoryFilterDTO splits this back into the
// start/end dates that drive the GreaterThanOrEqual/LessThanOrEqual bounds.
function formatRange(from, to) {
  if (!from) return '';
  var fromStr = moment(from).format(DISPLAY_FORMAT);
  if (!to) return fromStr;
  var toStr = moment(to).format(DISPLAY_FORMAT);
  if (fromStr === toStr) return fromStr;
  return fromStr + ' - ' + toStr;
}

function buildInitialSelected(parsed) {
  if (!parsed.from) return undefined;
  var selected = { from: parsed.from };
  if (parsed.to) selected.to = parsed.to;
  return selected;
}

class DateFilterPopover extends Component {
  constructor(props) {
    super(props);
    this.state = { selected: buildInitialSelected(parseFilterValue(props.value)) };
    this.popoverRef = React.createRef();
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.handleApply = this.handleApply.bind(this);
    this.handleClear = this.handleClear.bind(this);
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
    document.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  handleClickOutside(e) {
    if (this.popoverRef.current && !this.popoverRef.current.contains(e.target)) {
      this.props.onClose();
    }
  }

  handleKeyDown(e) {
    if (e.key === 'Escape') {
      this.props.onClose();
    } else if (e.key === 'Enter') {
      this.handleApply();
    }
  }

  handleSelect(range) {
    // react-day-picker range mode passes { from, to } or undefined.
    this.setState({ selected: range });
  }

  currentRange() {
    var sel = this.state.selected;
    if (!sel) return { from: null, to: null };
    return { from: sel.from || null, to: sel.to || null };
  }

  handleApply() {
    var range = this.currentRange();
    this.props.onApply(formatRange(range.from, range.to));
  }

  handleClear() {
    this.setState({ selected: undefined });
    this.props.onApply('');
  }

  popoverStyle() {
    var rect = this.props.anchorRect;
    if (!rect) return { width: POPOVER_WIDTH };
    var left = Math.max(VIEWPORT_MARGIN, rect.right - POPOVER_WIDTH);
    if (left + POPOVER_WIDTH > window.innerWidth - VIEWPORT_MARGIN) {
      left = window.innerWidth - POPOVER_WIDTH - VIEWPORT_MARGIN;
    }
    return { top: rect.bottom + POPOVER_OFFSET, left: left, width: POPOVER_WIDTH };
  }

  render() {
    var t = this.props.t;
    var anchorRect = this.props.anchorRect;
    if (!anchorRect) return null;

    var range = this.currentRange();
    var isRelativeValue = this.props.value === INSPECT_DUE_BEFORE_NOW || this.props.value === INSPECT_DUE_FROM_NOW;
    var relativeSummary = this.props.value === INSPECT_DUE_BEFORE_NOW ? 'Due Before Now' : 'Due Now and Later';
    var hasPickedRange = !!(range.from || range.to);
    var hasSelection = hasPickedRange || isRelativeValue;
    var summary = hasPickedRange
      ? formatRange(range.from, range.to)
      : (isRelativeValue ? relativeSummary : t('inspect:dateFilterEmptyHint'));
    var defaultMonth = range.from || range.to || new Date();

    return ReactDOM.createPortal(
      <div className="filter-popover-overlay">
        <div
          className="date-filter-popover"
          ref={this.popoverRef}
          style={this.popoverStyle()}
          role="dialog"
          aria-label={t('inspect:dateRangeDialogLabel')}
        >
          <div className={'date-filter-popover-summary' + (hasSelection ? '' : ' empty')}>
            {summary}
          </div>
          <div className="inspect-date-range-calendar date-filter-popover-calendar">
            <DayPicker
              mode="range"
              selected={this.state.selected}
              onSelect={this.handleSelect}
              captionLayout="dropdown"
              fromYear={2015}
              toYear={2035}
              defaultMonth={defaultMonth}
              showOutsideDays={false}
              fixedWeeks={false}
            />
          </div>
          <div className="date-filter-popover-footer">
            {hasSelection && (
              <button
                type="button"
                className="date-filter-popover-clear"
                onClick={this.handleClear}
              >
                {t('btn:clear')}
              </button>
            )}
            <button
              type="button"
              className="filter-popover-apply date-filter-popover-apply"
              onClick={this.handleApply}
            >
              {t('btn:apply')}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }
}

DateFilterPopover.propTypes = {
  anchorRect: PropTypes.object,
  value: PropTypes.string,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default withTranslation()(DateFilterPopover);
